import { useRef, type ReactNode, type RefObject } from 'react'
import { Button, Input, MultiSelect, Textarea, type ComboboxOption } from '@indipia/ui-core'
import { PartnerCard, type PartnerCardChanged, type PartnerCardPartner, type PartnerCardSent, type PartnerCardSignal, type PartnerCardTask, type PartnerSelection } from './PartnerCard'
import { TaskDetailCard } from './TaskDetailCard'

// 간트 공종 상세창(우측 슬라이드 패널) — GanttView 에서 분리(WP4-0).
// 상태·핸들러는 GanttView 가 들고 props 로 전달한다. 카드 순서: 발주·시공 방식 → 시공파트너 → 자재파트너 → 고객 요청 → 작업 요청 메모 → 선행공종(맨 아래).

/** 상세창 폭(px) — 열리면 우측 현장 패널이 접힌다(SchedulerPage) */
export const TASK_DETAIL_PANEL_WIDTH = 480

export interface TaskDetailPanelTask extends PartnerCardTask {
  workTypeName: string
  color: string
  /** 대기일(그 공정이 끝난 뒤 대기해야 하는 일수) — 간트 점선 박스와 같은 값 */
  leadTime: number
}

export interface PredecessorCandidate { taskId: number; workTypeName: string; color: string }

interface Props {
  /** 열림 여부 — 닫혀도 폭 트랜지션을 위해 마지막 공종으로 렌더를 유지한다 */
  open: boolean
  task: TaskDetailPanelTask | null

  workTypeName: string
  onWorkTypeNameChange: (v: string) => void
  /** 공종명 입력칸을 벗어날 때 저장 */
  onWorkTypeNameCommit: () => void

  // 시공파트너 카드
  partners: PartnerCardPartner[]
  partnerId: number | null
  partnerName: string
  onPartnerSelect: (sel: PartnerSelection) => void
  onPartnerSent: (sent: PartnerCardSent) => void
  changeReason: string
  onChangeReason: (reason: string) => void
  onPartnerChanged: (changed: PartnerCardChanged) => void
  /** 시공파트너 카드 상태 줄 점·사유 — 서버 반장 신호(폴백 없음) */
  foremanSignal: PartnerCardSignal
  /** SIF-1 '자재파트너가 시공' 공종 — 시공파트너 카드를 한 줄로 축약한다 */
  constructByVendor?: boolean
  /** 자재파트너가 시공일 때 표시할 업체명(자재파트너 거래처명) */
  constructVendorName?: string | null
  /** 시공파트너 카드 이력평가 줄·진행 요약 확장 지점(내용이 없으면 렌더하지 않음) */
  partnerHistorySlot?: ReactNode
  partnerProgressSlot?: ReactNode
  /** SIF-1 4-3 '발주·시공 방식' 카드 — 본문 맨 위(시공파트너 카드 앞)에 그대로 렌더한다(없으면 렌더 안 함) */
  orderModeCard?: ReactNode
  /** 자재파트너 카드 — 카드가 자체 TaskDetailCard 를 쓰므로 그대로 렌더한다(없으면 렌더 안 함) */
  materialPartnerCard?: ReactNode
  /** 고객 요청 카드(S16) — 자재파트너 카드 다음·작업 요청 메모 앞에 그대로 렌더한다(없으면 렌더 안 함) */
  customerRequestCard?: ReactNode

  predecessorCandidates: PredecessorCandidate[]
  predecessorIds: number[]
  onPredecessorChange: (ids: number[]) => void

  memo: string
  onMemoChange: (v: string) => void
  /** 메모 입력칸을 벗어날 때 저장 */
  onMemoCommit: () => void

  /** 기간 칩의 소요일(작업일 수) — 공휴일 정보를 쓸 수 없으면 null('–' 표시) */
  workdays: number | null
  /** 기간 수정 팝오버(A-15) — 기간 칩이 열고 닫는다. 팝오버 본체는 호스트가 만들어 넘긴다(앵커 = 기간 칩) */
  periodOpen: boolean
  onPeriodToggle: () => void
  /** [적용] 직후 기간 칩을 잠깐(1.6초) 강조 */
  periodFlash: boolean
  periodPopover?: (anchorRef: RefObject<HTMLElement | null>) => ReactNode

  /** 푸터 저장·발송 결과 문구 */
  notice: string
  error: string
  /** 실시간 저장 중 — 푸터에 '저장 중…' */
  saving: boolean
  onDelete: () => void
  onClose: () => void
}

const fmtMonthDay = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`
// 소요일 = 작업일 수(일요일·공휴일 제외) — 간트 좌측 소요일 칸과 같은 값을 호스트가 계산해 넘긴다(p.workdays)

export function TaskDetailPanel(p: Props) {
  const { task } = p
  // 기간 수정 팝오버의 앵커 — 기간 칩을 감싼 요소
  const periodChipRef = useRef<HTMLSpanElement | null>(null)
  // 선행공종 선택지 — 후보 + (후보에 없는 저장된 선택은 이름을 알 수 없어도 칩이 남도록 별도 항목)
  const predecessorOptions: ComboboxOption[] = [
    ...p.predecessorCandidates.map(t => ({ value: String(t.taskId), label: t.workTypeName })),
    ...p.predecessorIds
      .filter(id => !p.predecessorCandidates.some(t => t.taskId === id))
      .map(id => ({ value: String(id), label: '다른 공종' })),
  ]
  const colorByValue = new Map(p.predecessorCandidates.map(t => [String(t.taskId), t.color]))
  return (
    // width 는 항상 렌더되어야 최초 클릭 시에도 0→480 트랜지션이 재생된다
    <div style={{ width: p.open ? TASK_DETAIL_PANEL_WIDTH : 0, flexShrink: 0, overflow: 'hidden', transition: 'width .28s ease', willChange: 'width' }}>
      {task && (
        <div className="flex flex-col h-full overflow-hidden" style={{ width: TASK_DETAIL_PANEL_WIDTH, borderLeft: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-bg)' }}>
          {/* 머리 — 공종 색 점 + 공종명 + 기간 칩 + 닫기 */}
          <div className="flex items-start justify-between gap-2 px-4 py-3" style={{ borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)' }}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: task.color, flexShrink: 0 }} />
                <Input
                  value={p.workTypeName}
                  onMouseDown={e => e.stopPropagation()}
                  onChange={e => p.onWorkTypeNameChange(e.target.value)}
                  placeholder={task.workTypeName}
                  aria-label="공종명"
                  title="공종명을 사장님이 주로 쓰는 이름으로 바꿀 수 있습니다 — 비우면 표준 공종명으로 돌아갑니다"
                  width="fill"
                  className="flex-1 min-w-0"
                  style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)', border: 'none', background: 'transparent', padding: '2px 4px', borderRadius: 8 }}
                  onFocus={e => (e.currentTarget.style.background = 'var(--shell-bg)')}
                  onBlur={e => { e.currentTarget.style.background = 'transparent'; p.onWorkTypeNameCommit() }}
                />
              </div>
              <div className="mt-1" style={{ paddingLeft: 14 }}>
                {/* 기간 칩 — 눌러 기간 수정 팝오버를 연다(다시 누르면 닫힘). [적용] 직후 1.6초 강조 */}
                <span ref={periodChipRef} style={{ display: 'inline-block' }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="공사 기간 수정"
                    aria-haspopup="dialog"
                    aria-expanded={p.periodOpen}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={p.onPeriodToggle}
                    style={p.periodFlash ? { background: 'var(--gt-subtle)', color: 'var(--gt-primary)' } : undefined}
                  >
                    {`${fmtMonthDay(task.startDate)} ~ ${fmtMonthDay(task.endDate)} · ${p.workdays ?? '–'}일${task.leadTime > 0 ? ` · 대기 ${task.leadTime}일` : ''}`}
                  </Button>
                </span>
                {p.periodOpen && p.periodPopover?.(periodChipRef)}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              aria-label="닫기"
              onMouseDown={e => e.stopPropagation()}
              onClick={p.onClose}
            >×</Button>
          </div>

          {/* 본문 — 카드 순서: 발주·시공 방식 → 시공파트너 → 자재파트너 → 고객 요청 → 작업 요청 메모 → 선행공종(맨 아래) */}
          <div className="flex-1 overflow-y-auto px-4 py-3.5">
            {/* 방식에 따라 아래 시공파트너·자재파트너 카드가 바뀌므로 맨 위에 둔다 */}
            {p.orderModeCard ?? null}

            <PartnerCard
              task={task}
              selectedPartnerId={p.partnerId}
              selectedName={p.partnerName}
              partners={p.partners}
              onSelect={p.onPartnerSelect}
              onSent={p.onPartnerSent}
              changeReason={p.changeReason}
              onChangeReason={p.onChangeReason}
              onChanged={p.onPartnerChanged}
              historySlot={p.partnerHistorySlot}
              progressSlot={p.partnerProgressSlot}
              signal={p.foremanSignal}
              constructByVendor={p.constructByVendor}
              constructVendorName={p.constructVendorName}
            />

            {p.materialPartnerCard ?? null}

            {p.customerRequestCard ?? null}

            <TaskDetailCard title="작업 요청 메모">
              <Textarea
                value={p.memo}
                onMouseDown={e => e.stopPropagation()}
                onChange={e => p.onMemoChange(e.target.value)}
                onBlur={p.onMemoCommit}
                rows={3}
                placeholder="작업 요청 메모를 입력하세요"
                maxLength={1000}
                className="!max-w-none"
              />
              <div className="mt-1" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>작업요청을 보낼 때 반장님께 전달할 내용을 적어 주세요</div>
            </TaskDetailCard>

            <TaskDetailCard title="선행공종">
              {/* 여러 개를 고르거나 풀 수 있다(저장 때 선택한 전체 목록을 보냄). 칩은 공종명만, 공종색 점은 목록 항목에만 둔다.
                  후보는 자신·후행(순환)을 뺀 목록이며, 이미 저장된 선행은 후보 규칙과 무관하게 목록에 남는다(getPredecessorCandidates).
                  순환이 되는 조합은 서버가 INVALID_PREDECESSOR 로 거절한다. */}
              <div onMouseDown={e => e.stopPropagation()}>
                <MultiSelect
                  values={p.predecessorIds.map(String)}
                  onChange={values => p.onPredecessorChange(values.map(Number))}
                  options={predecessorOptions}
                  placeholder="선행공종을 골라 주세요"
                  emptyText="지정 가능한 선행이 없습니다"
                  renderOption={opt => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: colorByValue.get(opt.value) ?? 'var(--shell-hint)', flexShrink: 0 }} />
                      {opt.label}
                    </span>
                  )}
                />
              </div>
              <div className="mt-1" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>대기일은 공종별 표준 규칙이 있으면 자동 적용됩니다.</div>
            </TaskDetailCard>
          </div>

          {/* 푸터 — 저장·발송 결과 문구 · [이 공종 삭제](오른쪽). [저장] 버튼 없음 — 바꾸면 바로 저장된다(useGanttPanel#commitPanel) */}
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderTop: 'var(--border-width-hairline) solid var(--shell-border)', background: 'var(--shell-surface)' }}>
            <div className="flex-1 min-w-0" aria-live="polite">
              {p.error
                ? <div className="field error" style={{ maxWidth: 'none' }} role="alert"><span className="help">{p.error}</span></div>
                : p.saving
                  ? <div role="status" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>저장 중…</div>
                  : p.notice
                    ? <div role="status" style={{ fontSize: 12, color: 'var(--status-success-700)' }}>{p.notice}</div>
                    : <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>바꾸면 바로 저장돼요</div>}
            </div>
            <Button variant="danger" size="sm" onMouseDown={e => e.stopPropagation()} onClick={p.onDelete}>이 공종 삭제</Button>
          </div>
        </div>
      )}
    </div>
  )
}
